import {FetchBaseQueryError} from "@reduxjs/toolkit/query/react";
import {SerializedError} from "@reduxjs/toolkit";

export type ApiError = {
  error: FetchBaseQueryError | SerializedError,
  localized?: string
}

export const extractApiError = (error: FetchBaseQueryError | SerializedError): ApiError => {
  return {
    error: error,
    localized: extractApiErrorMessage(error),
  }
}

export const extractApiErrorMessage = (error: FetchBaseQueryError | SerializedError): string | undefined => {
  if ("data" in error && typeof error.data === "object" && error.data !== null) {
    return (error.data as { detail?: string }).detail;
  }
  return undefined;
}

/**
 * RTK Query's statuses for a request that never produced a server answer worth reading: the body was
 * not the JSON it claimed (an HTML error page from a proxy, a dev server's index.html), the network
 * failed, or it timed out. Their `error` is the JavaScript exception text -- "SyntaxError: Unexpected
 * token '<'", "TypeError: Failed to fetch" -- which is for a developer console, never for the person
 * using the screen. CUSTOM_ERROR is deliberately absent: its `error` is text the app wrote itself.
 */
const TRANSPORT_ERROR_STATUSES = new Set(['PARSING_ERROR', 'FETCH_ERROR', 'TIMEOUT_ERROR']);

const isTransportError = (error: object): boolean =>
  'status' in error && typeof error.status === 'string' && TRANSPORT_ERROR_STATUSES.has(error.status);

export const extractErrorMessage = (error: unknown, fallback = 'An unknown error occurred') : string => {

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    // Checked first: a PARSING_ERROR's `data` is the raw body it failed to parse, and its `error` is
    // the parser's complaint. Neither says anything a reader can act on, so the caller's own wording
    // is the honest thing to show.
    if (isTransportError(error)) {
      return fallback;
    }

    if ('data' in error && error.data && typeof error.data === 'object') {
      if ('detail' in error.data && typeof error.data.detail === 'string') {
        return error.data.detail;
      }
      if ('message' in error.data && typeof error.data.message === 'string') {
        return error.data.message;
      }
      if ('error' in error.data && typeof error.data.error === 'string') {
        return error.data.error;
      }
    }

    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
    if ('detail' in error && typeof error.detail === 'string') {
      return error.detail;
    }

    if ('response' in error && error.response && typeof error.response === 'object') {
      if ('data' in error.response && error.response.data && typeof error.response.data === 'object') {
        if ('detail' in error.response.data && typeof error.response.data.detail === 'string') {
          return error.response.data.detail;
        }
        if ('message' in error.response.data && typeof error.response.data.message === 'string') {
          return error.response.data.message;
        }
      }
    }
  }

  return fallback;
}
